import {
  Fragment, useState, useEffect, useCallback,
} from 'react';
import { useParams } from 'react-router-dom';
import {
  Tab,
  TabGroup,
  TabList,
  TabPanel,
  TabPanels,
} from '@headlessui/react';
import { useTranslation } from 'react-i18next';
import DOMPurify from 'dompurify';
import AddReviewModal from './components/add-review';
import ProductMedia from './components/media';
import ProductInfo from './components/info';
import FAQs from './components/faqs';
import ReviewsStatistics from './components/reviews-stats';
import ProductReviews from './components/reviews';
import ProductDetailsSkeleton from './components/product-details-skeleton';
import RetailProductsApiEndpoints from '../../services/retail_products.api';
import useLocaleRefresh from '../../hooks/use-locale-refresh';
import Alert from '../../components/alert';
import { Review } from '../../types/review';
import FloatingCTA from './components/floating-cta';

type AlertType = 'success' | 'error';

export default function ProductDetails() {
  const { slug } = useParams<{ slug: string }>();
  const [product, setProduct] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState<number>(1);
  const [alertData, setAlertData] = useState<{
  title: string;
  text: string;
  type: AlertType;
} | null>(null);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [totalReviews, setTotalReviews] = useState(0);
  const [reviewsLoading, setReviewsLoading] = useState(true);
  const [moreReviewsLoading, setMoreReviewsLoading] = useState(false);
  const [isFloatingCtaVisible, setIsFloatingCtaVisible] = useState(false);
  const { t } = useTranslation();
  const createMarkup = (html: string) => ({ __html: DOMPurify.sanitize(html) });

  const showAlert = useCallback((type: AlertType, translationKey: string) => {
    setAlertData({
      title: t(`${translationKey}.title`),
      text: t(`${translationKey}.body`),
      type,
    });
    setTimeout(() => setAlertData(null), 3000);
  }, [t]);

  const fetchProduct = useCallback(async () => {
    setLoading(true);
    try {
      if (!slug) throw new Error('Invalid product slug');
      const response = await RetailProductsApiEndpoints.getRetailProductBySlug(slug);
      setProduct(response.data);
    } catch (err) {
      // console.error(err instanceof Error ? err : new Error('An unknown error occurred'));
    } finally {
      setLoading(false);
    }
  }, [slug]);

  const fetchInitialReviews = useCallback(async (productId: string) => {
    setReviewsLoading(true);
    try {
      const response = await RetailProductsApiEndpoints.getRetailProductReviews(productId, 1, 2);
      setReviews(response.result);
      setTotalReviews(response.meta.total);
    } catch (err) {
      // console.error('Failed to fetch initial reviews:', err);
    } finally {
      setReviewsLoading(false);
    }
  }, []);

  const handleLoadMoreReviews = async () => {
    if (!product?.id || moreReviewsLoading) return;
    setMoreReviewsLoading(true);
    try {
      const newTotalSize = reviews.length + 6;

      const response = await RetailProductsApiEndpoints.getRetailProductReviews(
        product.id,
        1,
        newTotalSize,
      );

      setReviews(response.result);
    } catch (err) {
      // console.error('Failed to load more reviews:', err);
    } finally {
      setMoreReviewsLoading(false);
    }
  };

  useEffect(() => {
    fetchProduct();
  }, [fetchProduct]);

  useEffect(() => {
    const handleScroll = () => {
      const productInfoSection = document.getElementById('product-info');
      if (productInfoSection) {
        const { bottom } = productInfoSection.getBoundingClientRect();
        setIsFloatingCtaVisible(window.scrollY > 400 && bottom < 0);
      } else {
        setIsFloatingCtaVisible(window.scrollY > 400);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (!product?.id) return;
    fetchInitialReviews(product.id);
  }, [product?.id, fetchInitialReviews]);

  useLocaleRefresh(fetchProduct);

  const handleDecrement = (): void => setQuantity((prev) => (typeof prev === 'number' && prev > 1 ? prev - 1 : 1));

  const handleIncrement = (): void => setQuantity((prev) => (typeof prev === 'number' ? prev + 1 : 1));

  const handleReviewSuccess = () => showAlert('success', 'productDetails.reviews.submissionAlert.success');
  const handleUnauthorizedReview = () => showAlert('error', 'productDetails.reviews.submissionAlert.unauthorized');
  const handleAlreadyReviewed = () => showAlert('error', 'productDetails.reviews.submissionAlert.alreadyReviewed');
  const handleNoPurchase = () => showAlert('error', 'productDetails.reviews.submissionAlert.noPurchase');
  const handleAddToCartSuccess = () => showAlert('success', 'productDetails.reviews.submissionAlert.success');

  const CustomerFeedback = (
    <div className='mx-auto max-w-2xl px-4 sm:px-6 lg:max-w-7xl lg:px-0 pt-12 lg:pt-16'>
      <div className='flex flex-col gap-y-8'>
        {!loading && product && (
          <div className='order-1'>
            <ReviewsStatistics statistics={product.attributes.statistics} setIsReviewModalOpen={setIsReviewModalOpen} />
          </div>
        )}
        <div className='order-2'>
          <TabGroup>
            <div className='border-b border-gray-200'>
              <TabList className='-mb-px flex space-x-8 rtl:space-x-reverse'>
                <Tab className='whitespace-nowrap border-b-2 border-transparent pb-6 text-sm font-medium text-secondary hover:border-secondary data-[selected]:border-primary data-[selected]:text-primary focus:outline-none'>
                  {t('productDetails.customerFeedback')}
                </Tab>
                <Tab className='whitespace-nowrap border-b-2 border-transparent pb-6 text-sm font-medium text-secondary hover:border-secondary data-[selected]:border-primary data-[selected]:text-primary focus:outline-none'>
                  {t('productDetails.faq')}
                </Tab>
              </TabList>
            </div>
            <TabPanels as={Fragment}>
              <TabPanel>
                <h3 className='sr-only'>Customer Reviews</h3>
                {!reviewsLoading && reviews && (
                  <>
                    <ProductReviews reviews={reviews} />
                    {reviews.length < totalReviews && (
                      <div className='flex justify-center pt-4'>
                        <button
                          type='button'
                          onClick={handleLoadMoreReviews}
                          disabled={moreReviewsLoading}
                          className='inline-flex w-1/4 items-center justify-center rounded-md border border-transparent bg-primary px-8 py-3 h-11 text-base font-medium text-white shadow-sm hover:bg-lime-600 disabled:bg-lime-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-lime-600 whitespace-nowrap'
                        >
                          {moreReviewsLoading ? t('shared.loading') : t('productDetails.reviews.loadMore')}
                        </button>
                      </div>
                    )}
                  </>
                )}
              </TabPanel>
              <TabPanel className='text-sm text-secondary'>
                <h3 className='sr-only'>FAQs</h3>
                <FAQs />
              </TabPanel>
            </TabPanels>
          </TabGroup>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {loading && <ProductDetailsSkeleton />}
      {!loading && product && (
        <div className='bg-white py-10 sm:py-10'>
          <div className='mx-auto max-w-2xl px-4 sm:px-6 lg:max-w-7xl lg:px-0'>
            <div className='flex flex-col lg:grid lg:grid-cols-2 lg:items-start lg:gap-x-8'>
              <div className='order-2 lg:order-1 rtl:lg:order-2'>
                <div className='hidden lg:block'>
                  <ProductMedia images={product.attributes.attachments.images} videos={product.attributes.attachments.videos} />
                </div>
                <div className='hidden lg:block'>
                  {CustomerFeedback}
                </div>
              </div>
              <div className='flex flex-col order-1 lg:order-2 rtl:lg:order-1'>
                <div className='order-1'>
                  <ProductInfo
                    data={product.attributes}
                    productId={product.id}
                    handleDecrement={handleDecrement}
                    handleIncrement={handleIncrement}
                    quantity={quantity}
                    tag={product.attributes.tag}
                    tagColor={product.attributes.tag_color}
                  />
                </div>
                <div className='order-2 lg:hidden mt-12'>
                  <ProductMedia images={product.attributes.attachments.images} videos={product.attributes.attachments.videos} />
                </div>
                <div className='order-4 block lg:hidden'>
                  {CustomerFeedback}
                </div>
                <div className='order-3'>
                  <div className='mt-12 border-t border-gray-200 pt-8'>
                    <div className='prose prose-sm mt-3 text-secondary' dangerouslySetInnerHTML={createMarkup(product?.attributes?.description)} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      <AddReviewModal
        isOpen={isReviewModalOpen}
        setIsOpen={setIsReviewModalOpen}
        productId={product?.id}
        onSuccess={handleReviewSuccess}
        onUnauthorized={handleUnauthorizedReview}
        onAlreadyReviewed={handleAlreadyReviewed}
        onNoPurchase={handleNoPurchase}
      />
      {alertData && (
        <Alert
          title={alertData.title}
          text={alertData.text}
          isAlertVisible={!!alertData}
          onClose={() => setAlertData(null)}
          type={alertData.type}
        />
      )}
      {isFloatingCtaVisible && product && (
        <FloatingCTA
          quantity={quantity}
          price={product.attributes.base_price_with_vat}
          productId={product.id}
        />
      )}
    </>
  );
}
